import type { Dispatch } from '@reduxjs/toolkit'
import type { Business, User } from '@pbt/pbt-ui-components'

import type { FailedRefundTransaction } from '~/api/graphql/generated/types'
import i18n from '~/locales/i18n'

import { triggerErrorDoggo } from '../../../add-payment-dialog-v2/utils/errorDoggoUtils'
import { TransactionFailureReasonName } from '../../../add-payment-dialog-v2/utils/paymentErrorMapping'

/**
 * Error categories for refund transactions
 * Can evolve independently from payment error categories
 */
export enum RefundErrorCategory {
  CONNECTIVITY = 'connectivity',
  ISSUER_DECLINE = 'issuer_decline',
  USER_CANCEL = 'user_cancel',
  CONNECTIVITY_DOWN = 'connectivity_down',
  UNKNOWN = 'unknown',
  TRANSACTION_NOT_SETTLED = 'transaction_not_settled',
}

/**
 * Maps transaction failure reason name to refund error category
 * Uses the same failure reason names as payments but can have different category mappings in the future
 */
const FAILURE_REASON_TO_REFUND_CATEGORY_MAP: Record<
  TransactionFailureReasonName,
  RefundErrorCategory
> = {
  [TransactionFailureReasonName.DEVICE_OFFLINE]:
    RefundErrorCategory.CONNECTIVITY,
  [TransactionFailureReasonName.DEVICE_NOT_FOUND]:
    RefundErrorCategory.CONNECTIVITY,
  [TransactionFailureReasonName.DEVICE_TIMEOUT]:
    RefundErrorCategory.CONNECTIVITY,
  [TransactionFailureReasonName.DEVICE_BUSY]: RefundErrorCategory.CONNECTIVITY,
  [TransactionFailureReasonName.DEVICE_BATTERY_LOW]:
    RefundErrorCategory.CONNECTIVITY,
  [TransactionFailureReasonName.CANCELLED_BY_DEVICE]:
    RefundErrorCategory.USER_CANCEL,
  [TransactionFailureReasonName.CANCELLED_BY_API]:
    RefundErrorCategory.USER_CANCEL,
  [TransactionFailureReasonName.GATEWAY_REJECTED]:
    RefundErrorCategory.CONNECTIVITY_DOWN,
  [TransactionFailureReasonName.PROCESSOR_DECLINED]:
    RefundErrorCategory.ISSUER_DECLINE,
  [TransactionFailureReasonName.SETTLEMENT_DECLINED]:
    RefundErrorCategory.ISSUER_DECLINE,
  [TransactionFailureReasonName.UNKNOWN]: RefundErrorCategory.UNKNOWN,
  [TransactionFailureReasonName.TRANSACTION_NOT_SETTLED]:
    RefundErrorCategory.TRANSACTION_NOT_SETTLED,
}

const getRefundFailureReasonCategory = (
  failureReasonName: string,
): RefundErrorCategory =>
  FAILURE_REASON_TO_REFUND_CATEGORY_MAP[
    failureReasonName as TransactionFailureReasonName
  ] ?? RefundErrorCategory.UNKNOWN

/**
 * Gets translated message for refund error category
 * Translations are computed dynamically, so they update when i18n language changes.
 */
const getRefundCategoryTranslationMessage = (
  category: RefundErrorCategory,
  linked?: boolean,
): string => {
  switch (category) {
    case RefundErrorCategory.ISSUER_DECLINE:
      return linked
        ? i18n.t(
            'Dialogs:REFUND_DIALOG_V2.ERRORS.TRANSACTION_ISSUER_DECLINE_LINKED',
          )
        : i18n.t('Dialogs:REFUND_DIALOG_V2.ERRORS.TRANSACTION_ISSUER_DECLINE')
    case RefundErrorCategory.CONNECTIVITY:
      return i18n.t('Dialogs:REFUND_DIALOG_V2.ERRORS.TRANSACTION_CONNECTIVITY')
    case RefundErrorCategory.UNKNOWN:
      return i18n.t('Dialogs:REFUND_DIALOG_V2.ERRORS.TRANSACTION_UNKNOWN')
    case RefundErrorCategory.USER_CANCEL:
      return i18n.t('Dialogs:REFUND_DIALOG_V2.ERRORS.TRANSACTION_USER_CANCEL')
    case RefundErrorCategory.CONNECTIVITY_DOWN:
      return i18n.t(
        'Dialogs:REFUND_DIALOG_V2.ERRORS.TRANSACTION_CONNECTIVITY_DOWN',
      )
    case RefundErrorCategory.TRANSACTION_NOT_SETTLED:
      return i18n.t('Dialogs:REFUND_DIALOG_V2.ERRORS.TRANSACTION_NOT_SETTLED')
    default:
      return i18n.t('Dialogs:REFUND_DIALOG_V2.ERRORS.TRANSACTION_UNKNOWN')
  }
}

const getRefundTransactionFailureTranslationMessage = (
  failureReasonName: string,
  linked?: boolean,
): string => {
  const category = getRefundFailureReasonCategory(failureReasonName)

  try {
    return getRefundCategoryTranslationMessage(category, linked)
  } catch {
    // Fallback to unknown message if translation fails
    return getRefundCategoryTranslationMessage(RefundErrorCategory.UNKNOWN)
  }
}

/**
 * Extracts failure reason name from FailedRefundTransaction
 * Uses the name property which matches the backend TransactionFailureReason enum
 */
export const extractRefundFailureReasonName = (
  transaction: FailedRefundTransaction,
): string | null => {
  const failureReason = transaction.error?.failureReason

  if (!failureReason?.name) {
    return null
  }

  return failureReason.name
}

/**
 * Maps a FailedRefundTransaction to a user-friendly error message
 */
export const getRefundTransactionFailureMessage = (
  transaction: FailedRefundTransaction,
  linked?: boolean,
): string => {
  const failureReasonName = extractRefundFailureReasonName(transaction)

  if (failureReasonName === null) {
    // Fallback to generic message if we can't determine failure reason
    return i18n.t('Dialogs:REFUND_DIALOG_V2.ERRORS.TRANSACTION_UNKNOWN')
  }

  return getRefundTransactionFailureTranslationMessage(
    failureReasonName,
    linked,
  )
}

/**
 * Extracts backend error message from FailedRefundTransaction for error doggo
 * Returns backend message if available, otherwise returns null
 */
export const getRefundTransactionFailureBackendMessage = (
  transaction: FailedRefundTransaction,
): string | null => transaction.error?.message ?? null

/**
 * Maps refund initiation failure to user-friendly error message
 */
export const getRefundInitiationFailureMessage = (): string =>
  i18n.t('Dialogs:REFUND_DIALOG_V2.ERRORS.REFUND_INITIATION_FAILURE')

/**
 * GraphQL error types that can be returned from initiateRefundTransaction mutation
 */
export enum GraphQLRefundErrorType {
  TRANSACTION_NOT_SETTLED_ERROR = 'TRANSACTION_NOT_SETTLED_ERROR',
}

/**
 * Maps GraphQL error type to TransactionFailureReasonName for consistent error handling
 */
const GRAPHQL_ERROR_TYPE_TO_FAILURE_REASON: Record<
  GraphQLRefundErrorType,
  TransactionFailureReasonName
> = {
  [GraphQLRefundErrorType.TRANSACTION_NOT_SETTLED_ERROR]:
    TransactionFailureReasonName.TRANSACTION_NOT_SETTLED,
}

/**
 * Extracts error type from GraphQL error extensions
 * Returns the error type string if found in extensions.type
 */
export const extractGraphQLErrorType = (
  error: { extensions?: { type?: string } } | undefined,
): string | null => error?.extensions?.type ?? null

/**
 * Checks if a GraphQL error is a TRANSACTION_NOT_SETTLED_ERROR
 */
export const isTransactionNotSettledError = (
  errors: Array<{ extensions?: { type?: string } }> | undefined,
): boolean =>
  errors?.some(
    (error) =>
      extractGraphQLErrorType(error) ===
      GraphQLRefundErrorType.TRANSACTION_NOT_SETTLED_ERROR,
  ) ?? false

/**
 * Triggers error doggo for refund initiation errors, unless it's TRANSACTION_NOT_SETTLED.
 * User gets a specific message to try again in 24 hours for that case.
 */
export const triggerErrorDoggoForRefundInitiationErrorIfNeeded = ({
  errors,
  dispatch,
  backendErrorMessage,
  currentUser,
  currentBusiness,
}: {
  backendErrorMessage: string
  currentBusiness?: Business | null
  currentUser?: User | null
  dispatch: Dispatch<any>
  errors: Array<{ extensions?: { type?: string } }> | undefined
}): void => {
  if (isTransactionNotSettledError(errors)) {
    return
  }
  triggerErrorDoggo(
    dispatch,
    backendErrorMessage,
    500,
    currentUser,
    currentBusiness,
  )
}

/**
 * Gets the appropriate error message for a GraphQL error based on its type
 * Returns null if the error type is not recognized
 */
export const getGraphQLRefundErrorMessage = (
  errors: Array<{ extensions?: { type?: string } }> | undefined,
  linked?: boolean,
): string | null => {
  if (!errors?.length) return null

  const errorType = extractGraphQLErrorType(errors[0])
  if (!errorType) return null

  const failureReason =
    GRAPHQL_ERROR_TYPE_TO_FAILURE_REASON[errorType as GraphQLRefundErrorType]
  if (!failureReason) return null

  return getRefundTransactionFailureTranslationMessage(failureReason, linked)
}
